// Versão: 3.13
// Data..: 2026-10-14
// v3.13: prévia (hover) sempre visível na tela — posiciona pela ALTURA REAL do
// popover (ref) e sobe quando não cabe abaixo do cursor; reajusta a posição quando
// o conteúdo chega (useEffect), evitando que o rodapé seja cortado pela viewport.
// v3.12: prévia (hover) da lista também mostra o CONTEÚDO/descrição do chamado —
// busca sob demanda (GET /tickets/:id) no onMouseEnter com debounce e cache por
// linha; corpo convertido para texto plano (strip de HTML) e truncado.
// v3.11: "Selecionar todos" passa a marcar apenas os itens da PÁGINA ATUAL (não a
// lista filtrada inteira) — permite excluir/agir página a página.
// v3.10: exclusão em massa (ADMIN) via POST /tickets/bulk-delete em lotes de 50 —
// botão "Excluir selecionados" na barra de ações + Modal de confirmação com progresso.
// v3.09: ação "Excluir" (admin) por linha — Modal de confirmação avisa que apaga o
// chamado e todo o histórico (irreversível) e chama DELETE /tickets/:id.
// v3.10: linhas da lista coloridas por atribuição (staff) — verde: atribuído a mim;
// amarelo: a outro atendente; branco: sem atendente.
// v3.09: coluna "Empresa" na lista (staff) — mostra de qual empresa é o chamado,
// ordenável e presente também no card mobile.
// v3.08: botão "Mesclar" na ação em lote (2+ selecionados) — escolhe o destino e
// mescla os demais nele (reusa POST /tickets/:id/merge).
// v3.07: página em .full-bleed (ocupa toda a largura do painel, sem limite de 1600px).
// Lista de chamados: Tabela (status + filtros de prioridade/responsável/categoria) e Kanban.
// v3.02: seleção múltipla na tabela + ação em lote "Fechar selecionados" (staff).
// v3.03: coluna "Responsável" (staff) + prévia do chamado ao passar o mouse.
// v3.05: botão "Atribuir a mim" na linha (staff pega o chamado sem abrir o detalhe).
// v3.04: ordenação clicável nas colunas da tabela de chamados.
// v3.06: paginação da lista (25 por página) com controle ‹ 1 2 3 › e resumo;
// fechamento em lote agora abre modal exigindo a solução (aplicada a todos).
// v3.07: coluna "Responsável" destaca "✓ Você" quando o chamado é atribuído ao usuário logado.

import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { TicketStatus, UserRole, MessageDirection, PriorityLabels, type Ticket, type Category, type User, type Company, type Message, type Paginated, type BulkDeleteResult } from '@makweb/shared';
import { api } from '../lib/api.js';
import { buildCategoryOptions } from '../lib/categories.js';
import { config } from '../config.js';
import { getToken } from '../lib/auth.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import { StatusBadge, PriorityBadge } from '../components/Badge.js';
import { PageHeader } from '../components/PageHeader.js';
import { useSortable, SortableTh } from '../components/SortableTable.js';
import { Modal } from '../components/Modal.js';
import { RichEditor } from '../components/RichEditor.js';

const filters: { value: TicketStatus; label: string }[] = [
  { value: TicketStatus.OPEN, label: 'Abertos' },
  { value: TicketStatus.IN_PROGRESS, label: 'Em atendimento' },
  { value: TicketStatus.WAITING_CLIENT, label: 'Aguardando' },
  { value: TicketStatus.SCHEDULED, label: 'Agendados' },
  { value: TicketStatus.RESOLVED, label: 'Resolvidos' },
  { value: TicketStatus.CLOSED, label: 'Fechados' },
];

// Colunas do kanban (fluxo de trabalho).
const kanbanCols: { status: TicketStatus; label: string }[] = [
  { status: TicketStatus.OPEN, label: 'Abertos' },
  { status: TicketStatus.IN_PROGRESS, label: 'Em atendimento' },
  { status: TicketStatus.WAITING_CLIENT, label: 'Aguardando cliente' },
  { status: TicketStatus.RESOLVED, label: 'Resolvidos' },
];

const channelIcon: Record<string, string> = { web: '🌐', email: '✉', whatsapp: '💬', site_chat: '💭', mw_scan: '🖥', phone: '📞' };

// Fatia um array em blocos de no máximo `size` itens (preserva a ordem).
function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// Retorno de GET /tickets/:id (mesmo shape consumido por TicketDetailPage).
type TicketDetail = { ticket: Ticket; messages: Message[] };

// Converte HTML em texto plano para a prévia (NUNCA renderizar HTML cru no popover).
// Usa um elemento temporário (decodifica entidades) e normaliza espaços; depois
// trunca com reticências. Sem truncamento quando `max` <= 0.
function htmlToPreviewText(html: string, max = 320): string {
  const el = document.createElement('div');
  el.innerHTML = html;
  const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim();
  if (max > 0 && text.length > max) return `${text.slice(0, max).trimEnd()}…`;
  return text;
}

// Estado da prévia de conteúdo buscada sob demanda por linha.
type PreviewContent = { loading: boolean; body: string; error: boolean };

export function TicketsPage() {
  const { user } = useAuth();
  const toast = useToast();
  const isStaff = user?.role === UserRole.ADMIN || user?.role === UserRole.AGENT;
  const [view, setView] = useState<'table' | 'kanban'>('table');

  // Baixa o CSV autenticado (link direto não carrega o token).
  const exportCsv = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${config.apiUrl}/tickets/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Falha ao exportar');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'chamados.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exportado');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao exportar');
    }
  };

  return (
    <div className="full-bleed">
      <PageHeader
        eyebrow="Atendimento"
        title="Chamados"
        description="Gerencie e acompanhe os chamados de atendimento."
        actions={
          <>
            {isStaff && (
              <div className="seg">
                <button className={view === 'table' ? 'active' : ''} onClick={() => setView('table')}>☰ Lista</button>
                <button className={view === 'kanban' ? 'active' : ''} onClick={() => setView('kanban')}>▦ Kanban</button>
              </div>
            )}
            {isStaff && <button className="btn btn-sm" onClick={exportCsv}>⬇ Exportar CSV</button>}
            <Link to="/tickets/new" className="btn btn-primary">+ Novo chamado</Link>
          </>
        }
      />

      {view === 'table' || !isStaff ? <TableView isStaff={isStaff} /> : <KanbanView />}
    </div>
  );
}

function TableView({ isStaff }: { isStaff: boolean }) {
  const toast = useToast();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = (searchParams.get('status') as TicketStatus) || TicketStatus.OPEN;
  const [status, setStatus] = useState<TicketStatus>(initial);
  const [mine, setMine] = useState(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  // Filtros avançados (client-side sobre a lista carregada). Inicializados também a
  // partir da URL para permitir drill-down do painel gerencial (ex.: ?priority=high,
  // ?assignee=<id>, ?category=<id>).
  const [fPriority, setFPriority] = useState(searchParams.get('priority') ?? '');
  const [fAssignee, setFAssignee] = useState(searchParams.get('assignee') ?? '');
  const [fCategory, setFCategory] = useState(searchParams.get('category') ?? '');
  const [agents, setAgents] = useState<User[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  // Seleção múltipla (staff): ids marcados + ação de fechamento em lote.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkClosing, setBulkClosing] = useState(false); // modal de fechamento em lote
  // Exclusão em massa (ADMIN): modal de confirmação + progresso dos lotes.
  const [bulkDeleting, setBulkDeleting] = useState(false); // modal de exclusão em massa
  const [bulkDelBusy, setBulkDelBusy] = useState(false); // envio em andamento
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  // Paginação (client-side) da lista já filtrada/ordenada.
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const load = () => {
    setLoading(true);
    // Traz um lote amplo: busca, filtros, ordenação e paginação são feitos no cliente
    // sobre a lista completa (assim a busca/filtro enxergam todos os chamados, não só 1 página).
    const url = mine ? '/tickets?assignee=me&limit=500' : `/tickets?status=${status}&limit=500`;
    api.get<Paginated<Ticket>>(url).then((r) => setTickets(r.items)).catch(() => setTickets([])).finally(() => setLoading(false));
    if (!mine) setSearchParams({ status }, { replace: true });
  };

  useEffect(() => {
    load();
    setSelectedIds(new Set()); // limpa seleção ao trocar de aba/filtro de status
    setPage(1);
  }, [status, mine, setSearchParams]);

  // Limpa a seleção e volta à página 1 quando a busca/filtros mudam o conjunto visível
  // (evita contador divergente com itens fora da lista filtrada).
  useEffect(() => {
    setSelectedIds(new Set());
    setPage(1);
  }, [search, fPriority, fAssignee, fCategory]);

  useEffect(() => {
    if (!isStaff) return;
    api.get<Paginated<User>>('/agents').then((r) => setAgents(r.items)).catch(() => {});
    api.get<Paginated<Category>>('/categories').then((r) => setCategories(r.items)).catch(() => {});
    api.get<Paginated<Company>>('/companies').then((r) => setCompanies(r.items)).catch(() => {});
  }, [isStaff]);

  // Exclusão de chamado é exclusiva do admin (a autorização final fica no backend).
  const isAdmin = user?.role === UserRole.ADMIN;

  const categoryOptions = buildCategoryOptions(categories);
  // Mapas para exibir nomes legíveis (responsável, categoria e empresa) sem chamadas extras.
  const agentName = (id?: string) => (id ? agents.find((a) => a.id === id)?.name ?? '—' : '—');
  const categoryLabel = (id?: string) => (id ? categoryOptions.find((o) => o.id === id)?.label ?? '—' : '—');
  const companyName = (id?: string) => (id ? companies.find((c) => c.id === id)?.name ?? '—' : '—');

  const shown = tickets.filter((t) => {
    if (search.trim() && !`${t.number} ${t.subject} ${t.requesterName ?? ''}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (fPriority && t.priority !== fPriority) return false;
    if (fAssignee && (t.assigneeId ?? '') !== fAssignee) return false;
    if (fCategory && (t.categoryId ?? '') !== fCategory) return false;
    return true;
  });

  const hasAdvancedFilters = fPriority || fAssignee || fCategory;

  // Ordenação clicável da tabela (responsável/canal usam acessores para o texto exibido).
  const { sorted: sortedShown, sort } = useSortable<Ticket>(shown, {
    number: (t) => t.number,
    subject: (t) => t.subject,
    status: (t) => t.status,
    priority: (t) => t.priority,
    assignee: (t) => agentName(t.assigneeId),
    company: (t) => companyName(t.companyId),
    channel: (t) => t.channel,
    updatedAt: (t) => t.updatedAt,
  });

  // Paginação: fatia a lista ordenada na página atual. Corrige a página se ela
  // ficar fora do intervalo (ex.: após filtrar e reduzir o total).
  const totalPages = Math.max(1, Math.ceil(sortedShown.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageItems = sortedShown.slice(pageStart, pageStart + PAGE_SIZE);

  // ---- Seleção múltipla ----
  const selectable = isStaff;
  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // "Selecionar todos" marca apenas os itens da PÁGINA ATUAL (os visíveis na tela),
  // não a lista filtrada inteira. Assim o usuário remove/age página a página.
  const pageIds = pageItems.map((t) => t.id);
  const allShownSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someShownSelected = pageIds.some((id) => selectedIds.has(id));
  const toggleAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allShownSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Selecionados que estão na página atual (base para contador e ações em lote).
  const selectedShown = pageItems.filter((t) => selectedIds.has(t.id));
  // Já fechados não precisam ser fechados de novo.
  const closableIds = selectedShown.filter((t) => t.status !== TicketStatus.CLOSED).map((t) => t.id);

  // Fecha os selecionados aplicando a MESMA solução/causa a todos (fechamento em lote
  // exige o motivo, igual ao encerramento individual). Recebe os dados do modal.
  const doCloseSelected = async (cause: string, solution: string) => {
    if (closableIds.length === 0) return;
    setBulkBusy(true);
    const results = await Promise.allSettled(
      closableIds.map((id) => api.patch(`/tickets/${id}`, { status: TicketStatus.CLOSED, cause: cause || undefined, solution })),
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    const ok = results.length - failed;
    setBulkBusy(false);
    setBulkClosing(false);
    clearSelection();
    load();
    if (failed === 0) toast.success(`${ok} chamado(s) fechado(s).`);
    else toast.error(`${ok} fechado(s), ${failed} falharam.`);
  };

  // Mesclagem: escolhe um chamado destino; os demais selecionados viram origem
  // (são fechados e vinculados ao destino). Exige 2+ selecionados.
  const [merging, setMerging] = useState(false); // modal de mesclagem
  const [mergeTargetId, setMergeTargetId] = useState<string>('');
  const openMerge = () => {
    // Sugere como destino o de menor número (mais antigo) entre os selecionados.
    const sorted = [...selectedShown].sort((a, b) => a.number - b.number);
    setMergeTargetId(sorted[0]?.id ?? '');
    setMerging(true);
  };
  const doMerge = async () => {
    if (!mergeTargetId) return;
    const sourceTicketIds = selectedShown.filter((t) => t.id !== mergeTargetId).map((t) => t.id);
    if (sourceTicketIds.length === 0) { toast.error('Selecione ao menos um chamado além do destino.'); return; }
    setBulkBusy(true);
    try {
      await api.post(`/tickets/${mergeTargetId}/merge`, { sourceTicketIds });
      toast.success(`${sourceTicketIds.length} chamado(s) mesclado(s).`);
      setMerging(false);
      clearSelection();
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao mesclar');
    } finally {
      setBulkBusy(false);
    }
  };

  // Exclusão em massa (ADMIN): envia os ids selecionados em lotes de até 50
  // SEQUENCIAIS a POST /tickets/bulk-delete, acumulando excluídos e falhas sem
  // abortar o lote inteiro quando uma requisição falha. Ao final, recarrega a
  // lista, limpa a seleção e informa o total no toast.
  const doBulkDelete = async () => {
    const ids = selectedShown.map((t) => t.id);
    if (ids.length === 0) return;
    const batches = chunk(ids, 50);
    setBulkDelBusy(true);
    setBulkProgress({ done: 0, total: ids.length });
    let deleted = 0;
    const failed: Array<{ id: string; reason: string }> = [];
    let done = 0;
    for (const batch of batches) {
      try {
        const r = await api.post<BulkDeleteResult>('/tickets/bulk-delete', { ids: batch });
        deleted += r.deleted;
        failed.push(...r.failed);
      } catch (err) {
        // Falha do lote inteiro: registra todos os ids do lote como falhos e segue.
        const reason = err instanceof Error ? err.message : 'Falha na requisição';
        batch.forEach((id) => failed.push({ id, reason }));
      }
      done += batch.length;
      setBulkProgress({ done, total: ids.length });
    }
    setBulkDelBusy(false);
    setBulkDeleting(false);
    setBulkProgress(null);
    load();
    clearSelection();
    if (failed.length === 0) toast.success(`${deleted} chamado(s) excluído(s).`);
    else toast.error(`${deleted} excluído(s), ${failed.length} falharam.`);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {filters.map((f) => (
            <button key={f.value} className={`btn btn-sm ${!mine && status === f.value ? 'btn-primary' : 'btn-ghost'}`} onClick={() => { setMine(false); setStatus(f.value); }}>{f.label}</button>
          ))}
          {isStaff && (
            <button className={`btn btn-sm ${mine ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMine((v) => !v)}>★ Atribuídos a mim</button>
          )}
        </div>
        <input className="input" style={{ maxWidth: 240 }} placeholder="🔎 Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {isStaff && (
        <div className="flex gap-2 mb-4 flex-wrap items-center">
          <span className="text-dim text-sm">Filtros:</span>
          <select className="select" style={{ width: 150 }} value={fPriority} onChange={(e) => setFPriority(e.target.value)}>
            <option value="">Toda prioridade</option>
            {Object.entries(PriorityLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select className="select" style={{ width: 180 }} value={fAssignee} onChange={(e) => setFAssignee(e.target.value)}>
            <option value="">Qualquer responsável</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          {categoryOptions.length > 0 && (
            <select className="select" style={{ width: 180 }} value={fCategory} onChange={(e) => setFCategory(e.target.value)}>
              <option value="">Toda categoria</option>
              {categoryOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          )}
          {hasAdvancedFilters && (
            <button className="btn btn-sm btn-ghost" onClick={() => { setFPriority(''); setFAssignee(''); setFCategory(''); }}>Limpar</button>
          )}
          <span className="text-dim text-sm">{shown.length} de {tickets.length}</span>
        </div>
      )}

      {/* Barra de ações em lote (aparece quando há seleção visível). */}
      {selectable && selectedShown.length > 0 && (
        <div className="bulk-bar mb-4">
          <span className="bulk-count">{selectedShown.length} selecionado(s)</span>
          <div className="flex items-center gap-2" style={{ marginLeft: 'auto' }}>
            <button className="btn btn-sm btn-ghost" onClick={clearSelection} disabled={bulkBusy}>Limpar seleção</button>
            {selectedShown.length >= 2 && (
              <button className="btn btn-sm btn-secondary" onClick={openMerge} disabled={bulkBusy}>
                ⛙ Mesclar ({selectedShown.length})
              </button>
            )}
            <button className="btn btn-sm btn-danger" onClick={() => setBulkClosing(true)} disabled={bulkBusy || closableIds.length === 0}>
              {bulkBusy ? 'Fechando...' : `✓ Fechar selecionados${closableIds.length ? ` (${closableIds.length})` : ''}`}
            </button>
            {isAdmin && (
              <button className="btn btn-sm btn-danger" onClick={() => setBulkDeleting(true)} disabled={bulkBusy || bulkDelBusy}>
                🗑 Excluir selecionados ({selectedShown.length})
              </button>
            )}
          </div>
        </div>
      )}

      <div className="card card-flush">
        {loading ? (
          <div className="flex justify-center" style={{ padding: 40 }}><span className="spinner" /></div>
        ) : shown.length === 0 ? (
          <div className="empty"><div className="empty-icon">📭</div>Nenhum chamado.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                {selectable && (
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      aria-label="Selecionar todos"
                      checked={allShownSelected}
                      ref={(el) => { if (el) el.indeterminate = !allShownSelected && someShownSelected; }}
                      onChange={toggleAll}
                    />
                  </th>
                )}
                <SortableTh<Ticket> field="number" sort={sort}>#</SortableTh>
                <SortableTh<Ticket> field="subject" sort={sort}>Assunto</SortableTh>
                <SortableTh<Ticket> field="status" sort={sort}>Status</SortableTh>
                <SortableTh<Ticket> field="priority" sort={sort} className="hide-mobile">Prioridade</SortableTh>
                {isStaff && <SortableTh<Ticket> field="assignee" sort={sort} className="hide-mobile">Responsável</SortableTh>}
                {isStaff && <SortableTh<Ticket> field="company" sort={sort} className="hide-mobile">Empresa</SortableTh>}
                <SortableTh<Ticket> field="channel" sort={sort} className="hide-mobile">Canal</SortableTh>
                <SortableTh<Ticket> field="updatedAt" sort={sort} className="hide-mobile">Atualizado</SortableTh>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((t) => (
                <TicketRow
                  key={t.id}
                  ticket={t}
                  isStaff={isStaff}
                  isAdmin={isAdmin}
                  selectable={selectable}
                  selected={selectedIds.has(t.id)}
                  onToggle={() => toggleOne(t.id)}
                  assigneeName={agentName(t.assigneeId)}
                  categoryName={categoryLabel(t.categoryId)}
                  companyName={companyName(t.companyId)}
                  currentUserId={user?.id}
                  onChanged={load}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginação (aparece quando há mais de uma página). */}
      {!loading && sortedShown.length > PAGE_SIZE && (
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          totalItems={sortedShown.length}
          pageSize={PAGE_SIZE}
          onChange={setPage}
        />
      )}

      {/* Fechamento em lote: exige a solução (motivo), aplicada a todos os selecionados. */}
      {bulkClosing && (
        <BulkCloseModal
          count={closableIds.length}
          busy={bulkBusy}
          onCancel={() => setBulkClosing(false)}
          onConfirm={doCloseSelected}
        />
      )}

      {/* Exclusão em massa (ADMIN): confirmação irreversível com progresso dos lotes. */}
      <Modal
        open={bulkDeleting}
        onClose={() => { if (!bulkDelBusy) setBulkDeleting(false); }}
        title={`Excluir ${selectedShown.length} chamado(s)`}
        maxWidth={520}
        footer={
          <>
            <button className="btn btn-ghost" type="button" onClick={() => setBulkDeleting(false)} disabled={bulkDelBusy}>Cancelar</button>
            <button className="btn btn-danger" type="button" onClick={doBulkDelete} disabled={bulkDelBusy}>
              {bulkDelBusy ? 'Excluindo...' : 'Excluir chamados'}
            </button>
          </>
        }
      >
        <p className="text-muted text-sm" style={{ marginTop: 0 }}>
          Esta ação é <strong>irreversível</strong> e apaga em definitivo os{' '}
          <strong>{selectedShown.length} chamado(s)</strong> selecionados e todo o seu histórico.
        </p>
        {bulkProgress && (
          <p className="text-sm" style={{ marginBottom: 0 }}>
            Excluindo... {bulkProgress.done} de {bulkProgress.total}
          </p>
        )}
      </Modal>

      {/* Mesclagem: escolhe o chamado destino; os demais são mesclados nele. */}
      <Modal
        open={merging}
        onClose={() => setMerging(false)}
        title="Mesclar chamados"
        maxWidth={520}
        footer={
          <>
            <button className="btn btn-ghost" type="button" onClick={() => setMerging(false)}>Cancelar</button>
            <button className="btn btn-primary" type="button" disabled={bulkBusy || !mergeTargetId} onClick={doMerge}>
              {bulkBusy ? 'Mesclando...' : 'Mesclar'}
            </button>
          </>
        }
      >
        <p className="text-muted text-sm mb-3">
          Escolha o chamado <strong>destino</strong>. Os demais selecionados serão mesclados nele
          (fechados e vinculados ao destino). Esta ação não pode ser desfeita.
        </p>
        <div className="flex flex-col gap-2">
          {selectedShown.map((t) => (
            <label key={t.id} className="flex items-center gap-2 card" style={{ padding: 'var(--s3)', background: 'var(--surface-2)', cursor: 'pointer' }}>
              <input type="radio" name="merge-target" checked={mergeTargetId === t.id} onChange={() => setMergeTargetId(t.id)} />
              <span className="text-sm">
                <strong>#{t.number}</strong> — {t.subject}
                {mergeTargetId === t.id && <span className="badge badge-resolved" style={{ marginLeft: 6 }}>destino</span>}
              </span>
            </label>
          ))}
        </div>
      </Modal>
    </>
  );
}

// Modal de fechamento em lote: coleta a solução (obrigatória) e a causa (opcional)
// que serão aplicadas a todos os chamados selecionados.
function BulkCloseModal({
  count, busy, onCancel, onConfirm,
}: {
  count: number;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (cause: string, solution: string) => Promise<void>;
}) {
  const [cause, setCause] = useState('');
  const [solution, setSolution] = useState('');
  const isEmptyHtml = (html: string) => !html.replace(/<[^>]*>/g, '').trim() && !/<img/i.test(html);
  const solutionMissing = isEmptyHtml(solution);

  return (
    <Modal
      open
      title={`Fechar ${count} chamado(s)`}
      onClose={onCancel}
      maxWidth={560}
      footer={
        <>
          <button className="btn btn-ghost" type="button" onClick={onCancel} disabled={busy}>Cancelar</button>
          <button
            className="btn btn-danger"
            type="button"
            onClick={() => onConfirm(cause, solution)}
            disabled={solutionMissing || busy}
            title={solutionMissing ? 'Preencha a solução para continuar' : undefined}
          >
            {busy ? 'Fechando...' : 'Fechar chamados'}
          </button>
        </>
      }
    >
      <p className="text-muted text-sm" style={{ marginTop: 0 }}>
        A solução informada será registrada em <strong>todos os {count} chamados</strong> selecionados.
      </p>
      <div className="field">
        <label className="label">Causa (opcional)</label>
        <textarea className="textarea" rows={2} value={cause} onChange={(e) => setCause(e.target.value)} placeholder="O que originou os chamados" />
      </div>
      <div className="field mb-0">
        <label className="label">Solução <span style={{ color: 'var(--danger)' }}>*</span></label>
        <RichEditor value={solution} onChange={setSolution} placeholder="O que foi feito para resolver. Cole imagens ou anexe evidências." minHeight={140} />
        {solutionMissing && <p className="text-xs mt-1" style={{ color: 'var(--danger)', marginBottom: 0 }}>A solução é obrigatória para fechar os chamados.</p>}
      </div>
    </Modal>
  );
}

// Controle de paginação: ‹ 1 … 4 5 6 … 12 › + resumo "X–Y de Z".
function Pagination({
  page, totalPages, totalItems, pageSize, onChange,
}: {
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onChange: (p: number) => void;
}) {
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  // Gera as páginas a mostrar: sempre 1 e última, e uma janela ao redor da atual.
  const pages: (number | '…')[] = [];
  const add = (n: number) => { if (!pages.includes(n)) pages.push(n); };
  const window = 1;
  for (let p = 1; p <= totalPages; p++) {
    if (p === 1 || p === totalPages || (p >= page - window && p <= page + window)) {
      if (pages.length && typeof pages[pages.length - 1] === 'number' && (p as number) - (pages[pages.length - 1] as number) > 1) {
        pages.push('…');
      }
      add(p);
    }
  }

  return (
    <div className="flex items-center justify-between mt-4" style={{ flexWrap: 'wrap', gap: 'var(--s2)' }}>
      <span className="text-dim text-sm">{from}–{to} de {totalItems}</span>
      <div className="flex items-center gap-1">
        <button className="btn btn-sm btn-ghost" onClick={() => onChange(page - 1)} disabled={page <= 1}>‹</button>
        {pages.map((p, i) =>
          p === '…'
            ? <span key={`e${i}`} className="text-dim" style={{ padding: '0 6px' }}>…</span>
            : <button key={p} className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-ghost'}`} onClick={() => onChange(p)}>{p}</button>,
        )}
        <button className="btn btn-sm btn-ghost" onClick={() => onChange(page + 1)} disabled={page >= totalPages}>›</button>
      </div>
    </div>
  );
}

// Linha da tabela com prévia (popover) ao passar o mouse.
function TicketRow({
  ticket: t,
  isStaff,
  isAdmin,
  selectable,
  selected,
  onToggle,
  assigneeName,
  categoryName,
  companyName,
  currentUserId,
  onChanged,
}: {
  ticket: Ticket;
  isStaff: boolean;
  isAdmin: boolean;
  selectable: boolean;
  selected: boolean;
  onToggle: () => void;
  assigneeName: string;
  categoryName: string;
  companyName: string;
  currentUserId?: string;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [hover, setHover] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  // Ref ao popover para medir a altura real e reposicionar sem estourar a viewport.
  const previewRef = useRef<HTMLDivElement | null>(null);
  // Prévia do CONTEÚDO (descrição) buscada sob demanda ao abrir o popover.
  const [preview, setPreview] = useState<PreviewContent | null>(null);
  // Cache no próprio componente: uma vez buscado, não refaz a chamada ao reentrar.
  const previewFetched = useRef(false);
  // Timer do debounce para não buscar em hovers de passagem rápida.
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Busca o detalhe (GET /tickets/:id) e extrai a descrição = corpo da primeira
  // mensagem INBOUND (abertura do chamado) ou, na falta, a primeira disponível.
  const fetchPreview = async () => {
    if (previewFetched.current) return; // já em cache (ou já buscado)
    previewFetched.current = true;
    setPreview({ loading: true, body: '', error: false });
    try {
      const detail = await api.get<TicketDetail>(`/tickets/${t.id}`);
      const msgs = detail.messages ?? [];
      const first = msgs.find((m) => m.direction === MessageDirection.INBOUND) ?? msgs[0];
      const body = first?.body ? htmlToPreviewText(first.body, 360) : '';
      setPreview({ loading: false, body, error: false });
    } catch {
      // Permite tentar de novo numa próxima reentrada de hover.
      previewFetched.current = false;
      setPreview({ loading: false, body: '', error: true });
    }
  };

  // Agenda a busca ~300ms após o mouse entrar; cancela se o mouse sair antes.
  const schedulePreview = () => {
    if (previewFetched.current && preview && !preview.error) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => { void fetchPreview(); }, 300);
  };
  const cancelPreview = () => {
    if (previewTimer.current) { clearTimeout(previewTimer.current); previewTimer.current = null; }
  };
  // Limpa o timer pendente ao desmontar a linha.
  useEffect(() => () => { if (previewTimer.current) clearTimeout(previewTimer.current); }, []);
  // Quando a prévia abre ou o conteúdo chega (muda a altura), reajusta a posição
  // vertical para que o popover caiba inteiro na tela mesmo com o mouse parado.
  useEffect(() => {
    if (!hover || !previewRef.current) return;
    const margin = 12;
    const height = previewRef.current.offsetHeight;
    setPos((p) => {
      let y = p.y;
      if (y + height + margin > window.innerHeight) y = window.innerHeight - height - margin;
      y = Math.max(margin, y);
      return y === p.y ? p : { x: p.x, y };
    });
  }, [hover, preview]);
  // Exclusão (admin): modal de confirmação + estado de progresso.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Exclui o chamado em definitivo (apaga histórico). Fecha o modal e recarrega.
  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/tickets/${t.id}`);
      toast.success('Chamado excluído');
      setConfirmingDelete(false);
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao excluir');
    } finally {
      setDeleting(false);
    }
  };

  // Atendente "pega" o chamado: atribui a si mesmo sem abrir o detalhe.
  const assignToMe = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!currentUserId) return;
    setAssigning(true);
    try {
      await api.patch(`/tickets/${t.id}`, { assigneeId: currentUserId });
      toast.success(`Chamado #${t.number} atribuído a você`);
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao atribuir');
    } finally {
      setAssigning(false);
    }
  };
  const mineAlready = !!currentUserId && t.assigneeId === currentUserId;

  const [starting, setStarting] = useState(false);
  // Inicia o atendimento: muda o status para "Em atendimento" sem abrir o detalhe.
  const startAttendance = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setStarting(true);
    try {
      await api.patch(`/tickets/${t.id}`, { status: TicketStatus.IN_PROGRESS });
      toast.success(`Atendimento do chamado #${t.number} iniciado`);
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao iniciar atendimento');
    } finally {
      setStarting(false);
    }
  };
  // Só faz sentido iniciar quando ainda não está em atendimento nem encerrado.
  const canStart =
    isStaff &&
    t.status !== TicketStatus.IN_PROGRESS &&
    t.status !== TicketStatus.RESOLVED &&
    t.status !== TicketStatus.CLOSED;

  // Posiciona a prévia (fixed) perto do cursor, evitando corte por overflow da tabela.
  const onMove = (e: React.MouseEvent) => {
    const margin = 12;
    const width = 320;
    const gap = 14; // distância do cursor
    // Por padrão, à direita do cursor, coladinho. Se não couber, mostra à esquerda
    // do cursor (em vez de jogar para o canto da tela, o que a deixava longe).
    let x = e.clientX + gap;
    if (x + width + margin > window.innerWidth) x = e.clientX - gap - width;
    x = Math.max(margin, Math.min(x, window.innerWidth - width - margin));
    // Vertical: usa a ALTURA REAL da prévia (medida no DOM) para que ela caiba SEMPRE
    // inteira na tela. Tenta ancorar o topo no cursor; se o rodapé passar da viewport,
    // sobe a prévia (ancora o rodapé acima da borda inferior). Nunca sobe o topo além
    // da margem. Fallback de altura (~360) antes da 1ª medição.
    const maxH = window.innerHeight - margin * 2;
    const height = Math.min(previewRef.current?.offsetHeight || 360, maxH);
    let y = e.clientY + gap;
    if (y + height + margin > window.innerHeight) y = window.innerHeight - height - margin;
    y = Math.max(margin, y);
    setPos({ x, y });
  };

  // Cor de fundo por atribuição (staff): verde = atribuído a mim; amarelo = a outro
  // atendente; branco (sem classe) = sem atendente. A seleção em lote tem prioridade.
  const assignClass = !isStaff
    ? ''
    : mineAlready
      ? 'row-mine'
      : t.assigneeId
        ? 'row-other'
        : '';

  return (
    <tr
      className={`${selected ? 'row-selected' : assignClass}`.trim()}
      onMouseEnter={() => { setHover(true); schedulePreview(); }}
      onMouseLeave={() => { setHover(false); cancelPreview(); }}
      onMouseMove={onMove}
    >
      {selectable && (
        <td>
          <input
            type="checkbox"
            aria-label={`Selecionar chamado ${t.number}`}
            checked={selected}
            onChange={onToggle}
          />
        </td>
      )}
      <td className="text-dim">#{t.number}</td>
      <td>
        <div className="flex items-center gap-2">
          {isStaff && (
            mineAlready ? (
              <span className="badge badge-resolved" title="Atribuído a você" style={{ flexShrink: 0 }}>✓ Seu</span>
            ) : (
              <button
                className="btn btn-sm btn-ghost"
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                title="Atribuir este chamado a mim"
                onClick={assignToMe}
                disabled={assigning}
              >
                {assigning ? '...' : '✋ Atribuir a mim'}
              </button>
            )
          )}
          <Link to={`/tickets/${t.id}`} className="font-semibold" style={{ color: 'var(--text)' }}>{t.subject}</Link>
          {isAdmin && (
            <button
              className="btn btn-sm btn-ghost"
              style={{ flexShrink: 0, whiteSpace: 'nowrap', color: 'var(--danger)' }}
              title="Excluir este chamado (apaga todo o histórico)"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmingDelete(true); }}
            >
              🗑 Excluir
            </button>
          )}
        </div>
        {t.requesterName && <div className="text-dim text-xs">{t.requesterName}</div>}
        {hover && (
          <div ref={previewRef} className="ticket-preview" role="tooltip" style={{ left: pos.x, top: pos.y }}>
            <div className="ticket-preview-head">
              <span className="text-dim text-xs">#{t.number}</span>
              <PriorityBadge priority={t.priority} />
            </div>
            <div className="ticket-preview-title">{t.subject}</div>
            <div className="ticket-preview-grid">
              <span className="text-dim">Status</span><span><StatusBadge status={t.status} /></span>
              <span className="text-dim">Canal</span><span>{channelIcon[t.channel] ?? '•'} {t.channel}</span>
              {isStaff && (<><span className="text-dim">Responsável</span><span>{assigneeName}</span></>)}
              {isStaff && t.companyId && (<><span className="text-dim">Empresa</span><span>{companyName}</span></>)}
              <span className="text-dim">Categoria</span><span>{categoryName}</span>
              {t.requesterName && (<><span className="text-dim">Solicitante</span><span>{t.requesterName}</span></>)}
              <span className="text-dim">Atualizado</span><span>{new Date(t.updatedAt).toLocaleString('pt-BR')}</span>
              {t.dueAt && (<><span className="text-dim">Prazo</span><span>{new Date(t.dueAt).toLocaleString('pt-BR')}</span></>)}
            </div>
            {t.tags && t.tags.length > 0 && (
              <div className="ticket-preview-tags">
                {t.tags.map((tag) => <span key={tag} className="badge badge-normal">#{tag}</span>)}
              </div>
            )}
            {/* Conteúdo (descrição) buscado sob demanda — sempre texto plano. */}
            <div className="ticket-preview-content">
              <span className="text-dim text-xs">Conteúdo</span>
              {!preview || preview.loading ? (
                <div className="ticket-preview-body text-dim">Carregando conteúdo...</div>
              ) : preview.error ? (
                <div className="ticket-preview-body text-dim">Não foi possível carregar o conteúdo</div>
              ) : (
                <div className="ticket-preview-body">{preview.body || '(sem descrição)'}</div>
              )}
            </div>
          </div>
        )}
      </td>
      <td>
        <div className="flex items-center gap-2">
          <StatusBadge status={t.status} />
          {canStart && (
            <button
              className="btn btn-sm btn-primary"
              style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              title="Iniciar atendimento (muda o status para Em atendimento)"
              onClick={startAttendance}
              disabled={starting}
            >
              {starting ? '...' : '▶ Iniciar'}
            </button>
          )}
        </div>
      </td>
      <td className="hide-mobile"><PriorityBadge priority={t.priority} /></td>
      {isStaff && (
        <td className="hide-mobile">
          {t.assigneeId
            ? (mineAlready
                ? <span className="badge badge-resolved" title="Atribuído a você">✓ Você</span>
                : <span className="assignee"><span className="assignee-dot" />{assigneeName}</span>)
            : <span className="text-dim text-sm">Não atribuído</span>}
        </td>
      )}
      {isStaff && (
        <td className="hide-mobile text-muted text-sm">{t.companyId ? companyName : <span className="text-dim">—</span>}</td>
      )}
      <td className="hide-mobile">{channelIcon[t.channel] ?? '•'} <span className="text-dim text-sm">{t.channel}</span></td>
      <td className="hide-mobile text-dim text-sm">{new Date(t.updatedAt).toLocaleString('pt-BR')}</td>

      {/* Confirmação de exclusão (admin). Renderizado via Modal (portal no body). */}
      {isAdmin && (
        <Modal
          open={confirmingDelete}
          title={`Excluir chamado #${t.number}`}
          onClose={() => { if (!deleting) setConfirmingDelete(false); }}
          maxWidth={480}
          footer={
            <>
              <button className="btn btn-ghost" type="button" onClick={() => setConfirmingDelete(false)} disabled={deleting}>Cancelar</button>
              <button className="btn btn-danger" type="button" onClick={doDelete} disabled={deleting}>
                {deleting ? 'Excluindo...' : 'Excluir chamado'}
              </button>
            </>
          }
        >
          <p className="text-muted text-sm" style={{ marginTop: 0 }}>
            Esta ação apaga o chamado <strong>#{t.number} — {t.subject}</strong> e <strong>todo o histórico</strong>
            {' '}(mensagens, eventos, apontamentos, anexos). Não é possível desfazer.
          </p>
        </Modal>
      )}
    </tr>
  );
}

function KanbanView() {
  const toast = useToast();
  const [cols, setCols] = useState<Record<string, Ticket[]>>({});
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all(kanbanCols.map((c) => api.get<Paginated<Ticket>>(`/tickets?status=${c.status}&limit=100`).catch(() => ({ items: [] as Ticket[] }))))
      .then((results) => {
        const next: Record<string, Ticket[]> = {};
        kanbanCols.forEach((c, i) => (next[c.status] = results[i].items));
        setCols(next);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onDrop = async (status: TicketStatus) => {
    if (!dragId) return;
    const id = dragId;
    setDragId(null);
    // Move otimista na UI, depois persiste.
    try {
      await api.patch(`/tickets/${id}`, { status });
      toast.success('Chamado movido');
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Falha ao mover');
    }
  };

  if (loading) return <div className="flex justify-center" style={{ padding: 40 }}><span className="spinner" /></div>;

  return (
    <div className="grid" style={{ gridTemplateColumns: `repeat(${kanbanCols.length}, 1fr)`, gap: 'var(--s4)', alignItems: 'start' }}>
      {kanbanCols.map((c) => (
        <div
          key={c.status}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => onDrop(c.status)}
          style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius)', padding: 'var(--s3)', minHeight: 200 }}
        >
          <div className="flex items-center justify-between mb-4" style={{ padding: '4px 6px' }}>
            <strong className="text-sm">{c.label}</strong>
            <span className="badge badge-closed">{cols[c.status]?.length ?? 0}</span>
          </div>
          <div className="flex flex-col gap-2">
            {(cols[c.status] ?? []).map((t) => (
              <Link
                key={t.id}
                to={`/tickets/${t.id}`}
                draggable
                onDragStart={() => setDragId(t.id)}
                className="card"
                style={{ padding: 'var(--s3)', color: 'var(--text)', cursor: 'grab', display: 'block' }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-dim text-xs">#{t.number}</span>
                  <PriorityBadge priority={t.priority} />
                </div>
                <div className="font-semibold text-sm mt-2">{t.subject}</div>
                {t.requesterName && <div className="text-dim text-xs mt-2">{channelIcon[t.channel] ?? '•'} {t.requesterName}</div>}
              </Link>
            ))}
            {(cols[c.status]?.length ?? 0) === 0 && <div className="text-dim text-xs text-center" style={{ padding: 16 }}>Vazio</div>}
          </div>
        </div>
      ))}
    </div>
  );
}
